import './styles.css';
import React, { useEffect, useState } from 'react';
import { useRoomContext, RoomAudioRenderer } from '@livekit/components-react';
import { ConnectionState } from 'livekit-client';
import { useLogger } from '@ui/shared/components/Logger';
import { LOGGER_LABEL } from './utils/constants';

// Import custom hooks
import { useRecordingStart } from './hooks/useRecordingStart';
import { useTrackManagement } from './hooks/useTrackManagement';
import { useTrackAnalytics } from './hooks/useTrackAnalytics';

// Import components
import TracksContainer from './components/TracksContainer';

/**
 * Stack component
 *
 * This component renders a grid of video tracks for recording purposes.
 * It handles track management, recording initialization, and analytics.
 *
 * @returns {React.ReactElement} The rendered component
 */
const Stack = () => {
  const room = useRoomContext();
  const logger = useLogger();
  const [prevConnectionState, setPrevConnectionState] = useState(room?.state);
  const [reconnecting, setReconnecting] = useState(false);
  
  // Validate room exists
  useEffect(() => {
    if (!room) {
      logger.error(LOGGER_LABEL, 'Stack component: Room context is not available');
      return;
    }
    
    logger.info(LOGGER_LABEL, 'Stack component initialized', {
      roomName: room.name,
      roomState: room.state,
      participantCount: room.remoteParticipants.size
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room]);

  // Initialize recording when conditions are met
  useRecordingStart(room);

  // Manage tracks
  const { filteredTracks } = useTrackManagement(room,
    {
      attributesToWatch: ['participantSid'],
      onAttributeChange: (participant, changedKeys) => {
        logger.debug(LOGGER_LABEL, 'Participant attribute changed', {
          participantIdentity: participant.identity,
          participantSid: participant.sid,
          changedKeys
        });
      }
    }
  );

  // Track analytics for debugging and reporting
  useTrackAnalytics();

  // Monitor connection state changes
  useEffect(() => {
    if (!room) return;
    
    try {
      const currentState = room.state;
      
      if (
        prevConnectionState === ConnectionState.Disconnected &&
        currentState !== ConnectionState.Disconnected
      ) {
        logger.info(LOGGER_LABEL, 'Reconnected to room', {
          previousState: prevConnectionState,
          currentState,
          participantCount: room.remoteParticipants.size
        });
        setReconnecting(false);
      } else if (
        prevConnectionState !== ConnectionState.Disconnected &&
        currentState === ConnectionState.Disconnected
      ) {
        logger.warn(LOGGER_LABEL, 'Disconnected from room', {
          previousState: prevConnectionState,
          currentState
        });
        setReconnecting(true);
      } else if (prevConnectionState !== currentState) {
        logger.debug(LOGGER_LABEL, 'Connection state changed', {
          previousState: prevConnectionState,
          currentState
        });
      }

      setPrevConnectionState(currentState);
    } catch (error) {
      logger.error(LOGGER_LABEL, 'Error monitoring connection state', {
        error,
        errorMessage: error.message
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room, room?.state, prevConnectionState]);

  // Determine layout to use
  let main;
  
  if (!room) {
    logger.error(LOGGER_LABEL, 'Cannot render: Room is not available');
    main = <div className="no-videos-message">Room not available</div>;
  } else if (reconnecting) {
    logger.debug(LOGGER_LABEL, 'Rendering reconnecting UI');
    main = (
      <div className="reconnecting-message">
        <div className="reconnecting-spinner"></div>
        <div>Reconnecting to session...</div>
      </div>
    );
  } else if (room.state !== ConnectionState.Disconnected) {
    logger.debug(LOGGER_LABEL, 'Rendering tracks container', {
      trackCount: filteredTracks?.length || 0,
      roomState: room.state
    });
    main = <TracksContainer filteredTracks={filteredTracks} />;
  } else {
    logger.debug(LOGGER_LABEL, 'Rendering no videos message', {
      roomState: room.state
    });
    main = <div className="no-videos-message">No Videos</div>;
  }

  return (
    <div className="recording-stack">
      {main}
      <RoomAudioRenderer />
    </div>
  );
};

export default Stack;
